import { useEffect, useMemo, useState } from 'react';
import { Input, Stack, Text } from '@wapharma/design-system';
import {
  SOP_FAMILY_LABEL_FR,
  filterSops,
  getSop,
  type SopCatalogEntry,
  type SopFamily,
} from '@wapharma/shared';
import styles from './SopReferencesSection.module.css';

const FAMILY_FILTERS: Array<{ value: '' | SopFamily; label: string }> = [
  { value: '', label: 'Toutes' },
  { value: 'inbound', label: SOP_FAMILY_LABEL_FR.inbound },
  { value: 'sell', label: SOP_FAMILY_LABEL_FR.sell },
  { value: 'quality', label: SOP_FAMILY_LABEL_FR.quality },
  { value: 'docs', label: SOP_FAMILY_LABEL_FR.docs },
  { value: 'intake', label: SOP_FAMILY_LABEL_FR.intake },
  { value: 'later', label: SOP_FAMILY_LABEL_FR.later },
];

function Detail({ sop }: { sop: SopCatalogEntry }) {
  return (
    <article className={styles.detail} aria-labelledby={`sop-title-${sop.id}`}>
      <header className={styles.detailHead}>
        <span className={styles.shortTitle}>{sop.shortTitleFr}</span>
        {sop.later ? <span className={styles.laterBadge}>Plus tard</span> : null}
      </header>
      <h3 id={`sop-title-${sop.id}`} className={styles.detailTitle}>
        {sop.titleFr}
      </h3>
      <dl className={styles.dl}>
        <div>
          <dt>Qui</dt>
          <dd>{sop.ownerFr}</dd>
        </div>
        <div>
          <dt>Quand</dt>
          <dd>{sop.triggerFr}</dd>
        </div>
        <div>
          <dt>Trace</dt>
          <dd>{sop.recordFr}</dd>
        </div>
        <div>
          <dt>Famille</dt>
          <dd>{SOP_FAMILY_LABEL_FR[sop.family]}</dd>
        </div>
        {sop.reqIds?.length ? (
          <div>
            <dt>Exigences</dt>
            <dd className={styles.reqList}>
              {sop.reqIds.map((r) => (
                <code key={r}>{r}</code>
              ))}
            </dd>
          </div>
        ) : null}
      </dl>
      <Text size="xs" c="dimmed" className={styles.footnote}>
        Résumé d’équipe seulement. Ce n’est pas la procédure signée QMS.
      </Text>
    </article>
  );
}

export function SopReferencesSection({ initialSopId }: { initialSopId?: string | null }) {
  const [query, setQuery] = useState('');
  const [family, setFamily] = useState<'' | SopFamily>('');
  const [selectedId, setSelectedId] = useState<string | null>(initialSopId ?? null);

  useEffect(() => {
    if (!initialSopId) return;
    const row = getSop(initialSopId);
    if (row) setSelectedId(row.id);
  }, [initialSopId]);

  const rows = useMemo(() => filterSops(query, { family }), [query, family]);
  const selected = selectedId ? getSop(selectedId) : undefined;
  const shown = selected ?? rows[0] ?? null;

  useEffect(() => {
    if (selectedId && !rows.some((r) => r.id === selectedId) && rows[0]) {
      // Keep selection if filtered out — still show detail from getSop
      return;
    }
    if (!selectedId && rows[0]) setSelectedId(rows[0].id);
  }, [rows, selectedId]);

  return (
    <div className={styles.root} data-testid="sop-references">
      <Stack gap="xs" className={styles.intro}>
        <Text size="sm" fw={600}>
          Procédures
        </Text>
        <Text size="xs" c="dimmed">
          Qui agit, quand, quelle trace. Lecture seule pour toute l’équipe.
        </Text>
      </Stack>

      <div className={styles.toolbar}>
        <Input
          aria-label="Rechercher une SOP"
          placeholder="ID, titre, propriétaire…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        <div className={styles.familyPills} role="group" aria-label="Filtrer par famille">
          {FAMILY_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              className={family === f.value ? styles.pillOn : styles.pill}
              aria-pressed={family === f.value}
              onClick={() => setFamily(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.split}>
        <ul className={styles.list} role="listbox" aria-label="Catalogue des procédures">
          {rows.length === 0 ? (
            <li className={styles.empty}>Aucun résultat.</li>
          ) : (
            rows.map((s) => {
              const on = shown?.id === s.id;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={on}
                    className={on ? styles.rowOn : styles.row}
                    onClick={() => setSelectedId(s.id)}
                  >
                    <span className={styles.rowTitle}>{s.shortTitleFr}</span>
                    <span className={styles.rowSub}>{s.titleFr}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className={styles.detailPane}>
          {shown ? (
            <Detail sop={shown} />
          ) : (
            <Text size="sm" c="dimmed">
              Sélectionnez une procédure.
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
